import bog from 'bog'
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import styled, { withTheme, createGlobalStyle } from 'styled-components'

import * as TradingViewUtils from '../../utils/tradingview'
import ChartContainer, { CHART_HEIGHT } from './ChartContainer'
import LoadingShade from '../../components/common/LoadingShade'

// import $ from 'jquery'

const TRADINGVIEW_VIEW_NAME = 'TradingView'

const TV_CONTAINER_ID = 'tradingview_4bca8'
const TV_CANDLE_SERIES = 1
const TV_LINE_SERIES = 2
const TV_INTERVAL = 100 // For setting trading view script.
const TV_MAX_LOAD_CHECKS = 50
// const lightDarkFadeTime = 1;

const GlobalStyle = createGlobalStyle`
  .tv-loading-spinner,
  .ant-spin-container {
    flex: 1 1 auto;
    flex-basis: ${CHART_HEIGHT}px;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  }
  .tv-loading-spinner.hidden {
    display: none;
  }
`

const TradingViewChartContainer = styled(ChartContainer)`
  clip-path: ${({ theme }) => ''};
`

class TradingViewChartRenderer extends Component {
  static propTypes = {
    hidden: PropTypes.bool.isRequired,
    theme: PropTypes.object.isRequired,
    locale: PropTypes.string,
    symbol: PropTypes.string.isRequired,
    symbols: PropTypes.array,
    selectedView: PropTypes.string,
    tradingViewId: PropTypes.string,
    fullScreen: PropTypes.bool,
    interval: PropTypes.number,
    lineType: PropTypes.string,
    showIndicatorsDialog: PropTypes.bool,
  }

  static defaultProps = {
    locale: 'en',
    symbols: [],
    selectedView: 'default',
    fullScreen: false,
    interval: 60,
    lineType: '',
    tradingViewId: TV_CONTAINER_ID,
    showIndicatorsDialog: false,
  }

  state = { loading: false }

  _tv_interval = null
  _tv_symbol_timeout = null
  _tv_state_timeout = null

  componentDidMount() {
    if (!this._tv) {
      // Need to wait for setting the <script> tag for TradingView.
      let count = 0
      const { tradingViewId } = this.props
      this._tv_interval = setInterval(() => {
        if (window.TradingView) {
          this._resetTVInterval()
          if (document.getElementById(tradingViewId)) {
            this._init()
          }
        } else if (++count > TV_MAX_LOAD_CHECKS) this._resetTVInterval() // Give up
      }, TV_INTERVAL)
    }
  }

  componentDidUpdate({
    locale: prevLocale,
    symbol: prevSymbol,
    symbols: prevSymbols,
    lineType: prevLineType,
    theme: prevTheme,
    showIndicatorsDialog: prevShowIndicatorsDialog,
    interval,
  }) {
    const {
      locale,
      symbol,
      symbols,
      interval: currentInterval,
      lineType,
      theme,
      selectedView,
      showIndicatorsDialog,
    } = this.props
    this.lineType = lineType
    if (window.TradingView) {
      const tvInterval = TradingViewUtils.translateResolutionForTV(currentInterval)

      if (this._tv) {
        try {
          if (interval !== currentInterval && this._tv.activeChart().resolution() !== tvInterval) {
            this.setState({ loading: true })
            this._tv.activeChart().setResolution(tvInterval)
          } else if (symbol !== prevSymbol) {
            this.setState({ loading: true })
            this._tv.chart().setSymbol(symbol)
          } else if (
            symbols.length !== prevSymbols.length ||
            theme !== prevTheme ||
            locale !== prevLocale
          ) {
            this._tv && this._tv.remove()
            this._tv = null
          }
        } catch (err) {
          console.log(err)
          this._tv && this._tv.remove()
          this._tv = null
        }

        // line type activated?
        if (lineType !== prevLineType) {
          // this.setState({ loading: true });
          this.lineType = lineType
          if (lineType === 'Line') {
            // line
            if (prevLineType !== 'Line') {
              // switch to Candle form Line
              this._loadStudies(lineType)
            }
            this._tv.applyOverrides({ 'mainSeriesProperties.style': TV_LINE_SERIES })
          } else {
            // candles
            if (prevLineType === 'Line') {
              // switch to Line form Candle
              this._loadStudies(lineType)
            }
            this._tv.applyOverrides({ 'mainSeriesProperties.style': TV_CANDLE_SERIES })
          }
        }

        // show indicators dialog?
        // "Indicator" button clicked?
        if (showIndicatorsDialog !== prevShowIndicatorsDialog) {
          this._showIndicatorsDialog()
        }
      }

      // init tradingview?
      if (selectedView === TRADINGVIEW_VIEW_NAME) this._init()
    }
  }

  componentWillUnmount() {
    try {
      clearTimeout(this._tv_symbol_timeout)
      clearTimeout(this._tv_state_timeout)
      this._tv && this._tv.remove()
      this._tv = null
      this._tv_symbol_timeout = null
      this._tv_state_timeout = null
    } catch (err) {
      console.log(err)
    }
  }

  _init() {
    const {
      symbol,
      symbols,
      selectedView,
      locale,
      interval: currentInterval,
      lineType,
      theme,
      tradingViewId,
    } = this.props
    // init tradingview?
    if (!this._tv && selectedView === TRADINGVIEW_VIEW_NAME) {
      bog.info('Init TradingView chart.')
      const tvInterval = TradingViewUtils.translateResolutionForTV(currentInterval)
      this._tv = new window.TradingView.widget( // eslint-disable-line new-cap
        TradingViewUtils.generateTradingViewConfig({
          containerId: tradingViewId,
          symbol,
          symbols,
          interval: tvInterval,
          locale,
          theme,
        })
      )
      // const el = `#${tradingViewId}`;
      // typeof $ !== 'undefined' &&  $(el).css('opacity', '0')
      this.setState({ loading: true }) // eslint-disable-line react/no-did-update-set-state
      this._tv.onChartReady(() => {
        this._loadStudies(lineType)
        this._tv_symbol_timeout = setTimeout(() => {
          try {
            this._tv.setSymbol(symbol, tvInterval)
          } catch (err) {
            console.log(err)
          }
        }, 0)
        this._tv.applyOverrides(theme.tradingViewOverrides || {})
        if (lineType === 'Line') {
          this._tv.applyOverrides({ 'mainSeriesProperties.style': TV_LINE_SERIES })
        }
        // typeof $ !== 'undefined' && $(el).animate({ opacity: 1 }, lightDarkFadeTime / 2);
        this._tv
          .chart()
          .onDataLoaded()
          .subscribe(null, data => {
            this._tv_state_timeout = setTimeout(() => {
              this.setState({ loading: false })
            }, 0)
          })

        this._tv.subscribe('onAutoSaveNeeded', () => {
          this._tv.save(chartData => {
            localStorage['myTradingView' + this.props.theme.mode + this.lineType] = JSON.stringify(
              chartData
            )
          })
        })
      })
    }
  }

  _resetTVInterval() {
    clearInterval(this._tv_interval)
    this._tv_interval = null
  }

  _loadStudies = (lineType, mode = this.props.theme.mode) => {
    if (localStorage['myTradingView' + mode + lineType]) {
      this._tv.load(JSON.parse(localStorage['myTradingView' + mode + lineType]))
    } else if (lineType !== 'Line') {
      this._candleDefault()
    } else if (lineType === 'Line') {
      this._lineDefault()
    }
  }
  _candleDefault = () => {
    const { theme } = this.props
    this._tv.chart().createStudy('Moving Average', false, false, [7], null, {
      'Plot.linewidth': 1,
      'Plot.color': theme.tradingViewStyle.ma1,
    })
    this._tv.chart().createStudy('Moving Average', false, false, [25], null, {
      'Plot.linewidth': 1,
      'Plot.color': theme.tradingViewStyle.ma2,
    })
    this._tv.chart().createStudy('Moving Average', false, false, [99], null, {
      'Plot.linewidth': 1,
      'Plot.color': theme.tradingViewStyle.ma3,
    })
  }

  _lineDefault = () => {
    this._tv.chart().removeAllStudies()
    this._tv.chart().createStudy('Volume', false, false, [99], null, null)
  }

  _showIndicatorsDialog = () => {
    this._tv && this._tv.chart().executeActionById('insertIndicator')
  }

  render() {
    const { hidden, tradingViewId } = this.props
    const { loading } = this.state
    const showSpinner = !hidden && loading
    return (
      <Fragment>
        <LoadingShade hidden={!showSpinner} />
        <TradingViewChartContainer
          hidden={this.props.selectedView !== TRADINGVIEW_VIEW_NAME}
          fullScreen={this.props.fullScreen}
          selectedView={this.props.selectedView}
        >
          <div className="tradingview-widget-container">
            <div id={tradingViewId}>TradingView is loading&hellip;</div>
          </div>
        </TradingViewChartContainer>
        <GlobalStyle />
      </Fragment>
    )
  }
}

export default withTheme(TradingViewChartRenderer)
